import React, { FC, useCallback, useState } from 'react';
import { LegacyReminder } from 'src/interfaces/api/Collaborative';
import useCollaborative from 'src/hooks/api/useCollaborative';
import EntityTable from 'src/components/Table';
import { TableColumn } from 'src/components/Table/EntityTable';
import remindersState from 'src/recoil/atoms/reminders';
import { Order } from 'src/interfaces/Table';
import Ticket from 'src/interfaces/api/Ticket';
import data_criticity from 'src/data/criticity';
import { useSetRecoilState } from 'recoil';
import { ticketState } from 'src/recoil/selectors/tickets';
import useTicket from 'src/hooks/api/useTicket';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { IconButton } from '@material-ui/core';
import { MdDelete as DeleteIcon } from '@react-icons/all-files/md/MdDelete';

interface Props {
  hidden?: boolean;
  ticketId: Ticket['id'];
  remindersIds: LegacyReminder['id'][];
}

const columns: TableColumn<LegacyReminder>[] = [
  {
    id: 'title',
    type: 'text',
    label: 'Titre',
    link: (reminder: LegacyReminder) => `/dashboard/reminder/${reminder.id}`,
  },
  {
    id: 'closed',
    type: 'text',
    label: 'Status',
    renderValue: (reminder) => (reminder.closed ? 'Clôs' : 'Ouvert'),
  },
  {
    id: 'dateRecall',
    type: 'datetime',
    label: 'Date rappel',
    sortable: true,
  },
  {
    id: 'criticity',
    type: 'text',
    label: 'Criticité',
    sortable: true,
    renderValue: (reminder: LegacyReminder) => data_criticity[reminder.criticity]?.fr,
  },
];

const ReminderTable: FC<Props> = ({ ticketId, hidden, remindersIds }) => {
  const setTicket = useSetRecoilState(ticketState(ticketId));
  const { getReminders } = useCollaborative();
  const [reminderToRemove, setReminderToRemove] = useState<LegacyReminder['id']>();
  const { removeReminder } = useTicket();

  const handleRemoveReminder = async () => {
    if (reminderToRemove) {
      const ticket = await removeReminder(ticketId, reminderToRemove);
      setTicket(ticket);
      setReminderToRemove(undefined);
    }
  };

  const handleGetReminder = useCallback(
    async (page: number, limit: number, orderBy: keyof LegacyReminder, order: Order) => {
      return await getReminders(remindersIds || [], page, limit, orderBy, order);
    },
    [remindersIds]
  );

  return (
    <>
      <EntityTable
        hidden={hidden}
        columns={columns}
        defaultOrderBy='dateCreate'
        atomState={remindersState}
        actions={(reminder) => (
          <IconButton size='small' onClick={() => setReminderToRemove(reminder.id)}>
            <DeleteIcon />
          </IconButton>
        )}
        fetchEntitiesCallback={handleGetReminder}
      />

      <ConfirmationDialog
        title='Attention'
        confirmationText='Supprimer'
        description='Vous allez supprimer ce rappel. Êtes-vous sûr ?'
        onClose={() => setReminderToRemove(undefined)}
        open={!!reminderToRemove}
        onValidate={handleRemoveReminder}
      />
    </>
  );
};

export default React.memo(ReminderTable, (prevProps, nextProps) => {
  return prevProps.remindersIds === nextProps.remindersIds && prevProps.hidden === nextProps.hidden;
});
